import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { colors } from '../../utils/colors';
import { announceToScreenReader } from '../../utils/accessibility';

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const Label = styled.label`
  color: ${colors.textDark};
  font-size: 0.95rem;
  font-weight: 500;
`;

const InputBase = css`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.error ? colors.error : colors.border};
  border-radius: 8px;
  font-size: 1rem;
  color: ${colors.textDark};
  background: ${colors.white};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.error ? colors.error : colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.error ? colors.error : colors.primary};
    box-shadow: 0 0 0 3px ${props => 
      props.error ? `${colors.error}20` : colors.primaryLight};
  }

  &:disabled {
    background: ${colors.backgroundLight};
    cursor: not-allowed;
    opacity: 0.7;
  }

  &::placeholder {
    color: ${colors.textLight};
    opacity: 0.7;
  }
`;

const StyledInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['error'].includes(prop)
})`
  ${InputBase}
`;

const StyledTextArea = styled.textarea.withConfig({
  shouldForwardProp: (prop) => !['error', 'minHeight'].includes(prop)
})`
  ${InputBase}
  resize: vertical;
  min-height: ${props => props.minHeight || '100px'};
`;

const StyledSelect = styled.select.withConfig({
  shouldForwardProp: (prop) => !['error'].includes(prop)
})`
  ${InputBase}
  cursor: pointer;
  appearance: none;
  padding-right: 2.5rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%235a6c7d' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;

  &:hover {
    background-color: ${colors.backgroundLight};
  }
`;

const ErrorMessage = styled(motion.div)`
  color: ${colors.error};
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    font-size: 1rem;
  }
`;

const CheckboxWrapper = styled(FormGroup)`
  flex-direction: row;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.5rem 0;

  input {
    width: 20px;
    height: 20px;
    margin-top: 0.2rem;
  }

  label {
    flex: 1;
    cursor: pointer;
  }
`;

export const FormField = forwardRef(({ 
  label, 
  error, 
  id, 
  required, 
  type = 'text',
  ...props 
}, ref) => {
  const inputId = id || `field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const errorId = `${inputId}-error`;
  const hasError = !!error;

  const handleInvalid = (e) => {
    e.preventDefault();
    announceToScreenReader(error || 'Please check this field');
  };

  return (
    <FormGroup>
      <Label htmlFor={inputId}>
        {label}
        {required && <span className="sr-only"> (required)</span>}
      </Label>
      <StyledInput
        ref={ref}
        id={inputId}
        type={type}
        error={hasError}
        aria-invalid={hasError}
        aria-required={required}
        aria-describedby={hasError ? errorId : undefined}
        onInvalid={handleInvalid}
        {...props}
      />
      {hasError && (
        <ErrorMessage
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          id={errorId}
          role="alert"
        >
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
          {error}
        </ErrorMessage>
      )}
    </FormGroup>
  );
});

export const TextArea = forwardRef(({ 
  label, 
  error, 
  id, 
  required,
  ...props 
}, ref) => {
  const textareaId = id || `textarea-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const errorId = `${textareaId}-error`;
  const hasError = !!error;

  const handleInvalid = (e) => {
    e.preventDefault();
    announceToScreenReader(error || 'Please check this field');
  };

  return (
    <FormGroup>
      <Label htmlFor={textareaId}>
        {label}
        {required && <span className="sr-only"> (required)</span>}
      </Label>
      <StyledTextArea
        ref={ref}
        id={textareaId}
        error={hasError}
        aria-invalid={hasError}
        aria-required={required}
        aria-describedby={hasError ? errorId : undefined}
        onInvalid={handleInvalid}
        {...props}
      />
      {hasError && (
        <ErrorMessage
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          id={errorId}
          role="alert"
        >
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
          {error}
        </ErrorMessage>
      )}
    </FormGroup>
  );
});

export const Select = forwardRef(({ 
  label, 
  error, 
  id, 
  required, 
  options,
  ...props 
}, ref) => {
  const selectId = id || `select-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const errorId = `${selectId}-error`;
  const hasError = !!error;

  return (
    <FormGroup>
      <Label htmlFor={selectId}>
        {label}
        {required && <span className="sr-only"> (required)</span>}
      </Label>
      <StyledSelect
        ref={ref}
        id={selectId}
        error={hasError}
        aria-invalid={hasError}
        aria-required={required}
        aria-describedby={hasError ? errorId : undefined}
        {...props}
      >
        <option value="">Seleccionar...</option>
        {options.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </StyledSelect>
      {hasError && (
        <ErrorMessage
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          id={errorId}
          role="alert"
        >
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
          {error}
        </ErrorMessage>
      )}
    </FormGroup>
  );
});

export const Checkbox = forwardRef(({ 
  label, 
  error, 
  id, 
  required,
  ...props 
}, ref) => {
  const checkboxId = id || `checkbox-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const errorId = `${checkboxId}-error`;
  const hasError = !!error;

  return (
    <CheckboxWrapper>
      <input
        ref={ref}
        type="checkbox"
        id={checkboxId}
        aria-invalid={hasError}
        aria-required={required}
        aria-describedby={hasError ? errorId : undefined}
        {...props}
      />
      <Label htmlFor={checkboxId}>
        {label}
        {required && <span className="sr-only"> (required)</span>}
      </Label>
      {hasError && (
        <ErrorMessage
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          id={errorId}
          role="alert"
        >
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
          {error}
        </ErrorMessage>
      )}
    </CheckboxWrapper>
  );
});